<template>
  <AppModal v-if="prominent" :model-value="isUndecided" :title="t('analytics_consent.shared_title')" title-class="text-xl" :show-close="false" :close-on-backdrop="false" @cancel="decline">
    <p class="text-sm leading-relaxed text-theme-text-secondary">{{ t('analytics_consent.shared_body') }}</p>
    <a :href="privacyHref || '/#/privacy-statement'" class="mt-3 inline-block text-sm underline">{{ t('analytics_consent.privacy_link') }}</a>
    <div class="mt-5 grid gap-3 sm:grid-cols-2">
      <AppButton data-test="analytics-consent-decline" tone="neutral" variant="outline" :label="t('analytics_consent.shared_decline')" @click="decline" />
      <AppButton data-test="analytics-consent-accept" tone="neutral" variant="outline" :label="t('analytics_consent.shared_accept')" @click="accept" />
    </div>
  </AppModal>
  <transition v-else name="nav-fade">
    <div
      v-if="isUndecided"
      data-test="analytics-consent-banner"
      class="fixed inset-x-0 bottom-0 z-toast p-3 sm:p-4"
      role="region"
      :aria-label="t('analytics_consent.aria_label')"
    >
      <div
        class="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border border-theme-border bg-theme-surface/95 p-4 shadow-theme-dialog backdrop-blur
          sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <p class="text-sm font-semibold text-theme-text">
            {{ t('analytics_consent.title') }}
          </p>
          <p class="mt-1 text-xs leading-snug text-theme-text-muted">
            {{ t('analytics_consent.body') }}
            <a
              v-if="privacyHref"
              :href="privacyHref"
              class="underline underline-offset-2 hover:text-theme-text"
            >
              {{ t('analytics_consent.privacy_link') }}
            </a>
            <router-link
              v-else
              :to="{ name: 'privacy-statement' }"
              class="underline underline-offset-2 hover:text-theme-text"
            >
              {{ t('analytics_consent.privacy_link') }}
            </router-link>
          </p>
        </div>
        <div class="flex flex-shrink-0 items-center gap-2">
          <app-button
            data-test="analytics-consent-decline"
            variant="ghost"
            size="sm"
            @click="decline"
          >
            {{ t('analytics_consent.decline') }}
          </app-button>
          <app-button
            data-test="analytics-consent-accept"
            tone="primary"
            size="sm"
            @click="accept"
          >
            {{ t('analytics_consent.accept') }}
          </app-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppModal from '@/components/ui/overlays/AppModal.vue';
  import { useAnalyticsConsent } from '@/composables/useAnalytics';

  // The website has no portal router; it passes its privacy statement URL instead.
  defineProps({ privacyHref: { type: String, default: '' }, prominent: { type: Boolean, default: false } });
  const { t } = useI18n();
  const { consent, grant, deny } = useAnalyticsConsent();

  // Only prompt when the user has not yet decided (null). Once granted/denied
  // the banner stays hidden; the choice can be changed from the privacy page.
  const isUndecided = computed(() => consent.value !== 'granted' && consent.value !== 'denied');

  function accept() {
    grant();
  }

  function decline() {
    deny();
  }
</script>
